import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useOrgData } from './OrgDataContext';
import {
  DEFAULT_DATA_RANGE, LIVE_DATA_RANGE, loadLayout, loadLayoutData, REBA_FIELDS,
  REBA_THRESHOLDS_INFLUXDB, saveLayoutData,
  VIEW_MODE_DETAILS,
  VIEW_MODE_GLOBAL
} from "../services/ergonomics/ergonomics-service";
import {
  formatExpositionData, formatMaxData, formatSeriesByField,
  getExpositionQuery,
  getGlobalQuery, getMaxQuery,
  getQuery
} from "../services/ergonomics/ergonomics-requests";
import { getInfluxdbData } from "../services/api/influxdb-tools";
import { useGetData } from "../services/api/api-tools";
import { useLocation } from "react-router-dom";
import { formatSecondsToHMS, formatSecondsToHoursMinutesSeconds } from "../helpers/date/date-helper";

export const ErgonomicsContext = createContext();

export const ErgonomicsProvider = ({ children }) => {

  const { workstationSelected } = useOrgData();

  const filters = useGetData('ergonomics_filters', 'ergonomics_filters');

  const [isLoading, setIsLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [checkboxes, setCheckboxes] = useState({});
  const [data, setData] = useState([]);
  const [unzoomedData, setUnzoomedData] = useState(null);
  const [expositionData, setExpositionData] = useState([]);
  const [maxData, setMaxData] = useState([]);
  const [humansNumber, setHumansNumber] = useState(null);
  const [showRight, setShowRight] = useState(false);
  const isLiveMode = new URLSearchParams(useLocation().search).has('live');
  const [selectedDateRange, setSelectedDateRange] = useState(isLiveMode ? LIVE_DATA_RANGE : DEFAULT_DATA_RANGE);
  const [zoomDateRange, setZoomDateRange] = useState(null); // TODO Manage reset
  const [hoveredTimestamp, setHoveredTimestamp] = useState(null);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const firstLoaded = useRef(false);

  const viewMode = zoomDateRange === null || (zoomDateRange.stop - zoomDateRange.start) / 1000 > 2 * 60 * 60 ? VIEW_MODE_GLOBAL : VIEW_MODE_DETAILS;
  const side = showRight ? 'right' : 'left';

  const viewModeLabel = useMemo(() => {
    if (!zoomDateRange) return null;

    const { start, stop } = zoomDateRange;
    const startDate = new Date(start);
    const stopDate = new Date(stop);

    const diffHours = Math.abs(stop - start) / (1000 * 60 * 60);

    let text = (diffHours >= 12
      ? "vue à la journée"
      : diffHours > 2
        ? `vue à l'heure (${startDate.getHours()}h - ${stopDate.getHours()}h)`
        : "vue détaillée").toUpperCase();

    text += ' - 1 BARRE = ' + formatSecondsToHMS(zoomDateRange.window_s);

    if (!isNaN(humansNumber) && humansNumber !== null)
      text += ' - ' + humansNumber + ' personne(s) observée(s)';

    return text;
  }, [selectedDateRange, zoomDateRange, humansNumber]);

  useEffect( () => {
    let timer = null;

    // Save current layout data
    let newLayoutData;
    if (!firstLoaded.current) newLayoutData = { ...layoutData, start: selectedDateRange.start, stop: selectedDateRange.stop, zoomDateRange: null };
    else newLayoutData = { ...layoutData, start: selectedDateRange.start, stop: selectedDateRange.stop };
    setLayoutData(newLayoutData);
    saveLayoutData(newLayoutData);

    const getData = async () => {
      if (!workstationSelected) return;

      setIsLoading(true);

      const range = zoomDateRange !== null ? zoomDateRange : selectedDateRange;

      let fullRequest = (viewMode === VIEW_MODE_GLOBAL) ?
        getGlobalQuery(workstationSelected.id, fields, range.window, side) :
        getQuery(workstationSelected.id, fields, range.window, side);

      try {
        let newData = await getInfluxdbData(
          'engine',
          'reba_score',
          [],
          [],
          Math.floor(range.start / 1000),
          Math.floor(range.stop / 1000),
          null,
          null,
          null,
          [],
          null,
          fullRequest,
          false,
          null,
        );
        setHumansNumber(Math.max(...newData.map(o => o.human_id_max)) + 1);
        newData = formatSeriesByField(newData, fields, -1);
        setData(newData);
        if (zoomDateRange === null) {
          setUnzoomedData(newData);
        }
      }
      catch (error) {
        setData([]);
        setExpositionData([]);
        setMaxData([]);
        setIsLoading(false);
        return;
      }

      if (!isLiveMode && viewMode === VIEW_MODE_DETAILS) {
        // Exposition data
        let newExpositionData = await getInfluxdbData(
          'engine',
          'reba_score',
          [],
          [],
          Math.floor(range.start / 1000),
          Math.floor(range.stop / 1000),
          null,
          null,
          null,
          [],
          null,
          getExpositionQuery(workstationSelected.id, REBA_THRESHOLDS_INFLUXDB, side),
          false,
          null,
        );
        newExpositionData = formatExpositionData(newExpositionData, fields);
        setExpositionData(newExpositionData);

        // Max data
        let newMaxData = await getInfluxdbData(
          'engine',
          'reba_score',
          [],
          [],
          Math.floor(range.start / 1000),
          Math.floor(range.stop / 1000),
          null,
          null,
          null,
          [],
          null,
          getMaxQuery(workstationSelected.id, fields, range.window, side),
          false,
          null,
        );
        newMaxData = formatMaxData(newMaxData, fields);
        setMaxData(newMaxData);
      }
      else {
        setExpositionData([]);
        setMaxData([]);
      }

      firstLoaded.current = true;
      setIsLoading(false);
    };

    getData();

    if (isLiveMode) {
      timer = window.setInterval(() => {
        const nowLive = new Date();
        setSelectedDateRange({...selectedDateRange, start: nowLive.getTime() - 5 * 60 * 1000, stop: nowLive.getTime()})
      }, 10_000);
    }

    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, [fields, selectedDateRange, zoomDateRange, workstationSelected, showRight]);


  useEffect(() => {
    if (filters.isLoading || filters.isError) return;

    // Manage fields
    setFields([...REBA_FIELDS, ...filters.data.map((filter) => filter.name), "human_id"]);

    // Manage checkboxes
    setCheckboxes((prev) => ({
      ...prev,
      ...filters.data.reduce((acc, filter) => {
        acc[filter.name] = false;
        return acc;
      }, {})
    }));
  }, [filters.isLoading, filters.isError]);

  const flags = useMemo(() => {
    if (!unzoomedData || Object.keys(unzoomedData).length === 0) return [];

    const activeFilters = Object.keys(checkboxes).filter(key => checkboxes[key] && key in unzoomedData);
    if (activeFilters.length === 0) return Object.values(unzoomedData)[0]?.map(() => true) || [];

    return unzoomedData[activeFilters[0]].map((_, i) =>
      activeFilters.every(key => {
        const v = unzoomedData[key]?.[i]?.value;
        return v !== 0 || v === null;
      })
    );
  }, [unzoomedData, checkboxes]);


  const [layout, setLayout] = useState(() =>
    loadLayout() || [
      { i: 'main-graphs', x: 0, y: 0, w: 3, h: 1, maxW: 4 },
      { i: 'timeline', x: 0, y: 1, w: 3, h: 1, maxW: 4 },
      { i: 'notes', x: 3, y: 0, w: 1, h: 2, maxW: 4},
      { i: 'synthesis', x: 3, y: 2, w: 1, h: 2, maxW: 4},
      { i: 'main-details-graphs', x: 0, y: 3, w: 3, h: 1, maxW: 4},
      { i: 'details-graphs', x: 0, y: 4, w: 3, h: 1, maxW: 4},
      { i: 'data', x: 0, y: 5, w: 2, h: 2, maxW: 4 },
      { i: 'data-requests', x: 2, y: 5, w: 2, h: 2, maxW: 4 },
    ]
  );

  const [layoutData, setLayoutData] = useState(() => loadLayoutData() || {});

  const hasData = Object.keys(data)?.length > 0;

  const value = useMemo(
    () => ({
      data, unzoomedData, maxData, expositionData,
      flags, filters, fields, side, isLiveMode, viewMode, viewModeLabel,
      selectedDateRange, setSelectedDateRange,
      zoomDateRange, setZoomDateRange,
      hoveredTimestamp, setHoveredTimestamp,
      checkboxes, setCheckboxes, showRight, setShowRight,
      layout, setLayout, layoutData, setLayoutData,
      isEditingLayout, setIsEditingLayout,
      isLoading, hasData
    }),
    [
      data, unzoomedData, maxData, expositionData,
      flags, filters, fields, side, isLiveMode, viewMode, viewModeLabel,
      selectedDateRange, setSelectedDateRange,
      zoomDateRange, setZoomDateRange,
      hoveredTimestamp, setHoveredTimestamp,
      checkboxes, setCheckboxes, showRight, setShowRight,
      layout, setLayout, layoutData, setLayoutData,
      isEditingLayout, setIsEditingLayout,
      isLoading, hasData
    ],
  );

  return <ErgonomicsContext.Provider value={value}>{children}</ErgonomicsContext.Provider>;
};

export const useErgonomicsContext = () => {
  const context = useContext(ErgonomicsContext);
  if (!context) {
    throw new Error('useErgonomicsContext should be used on ErgonomicsProvider');
  }
  return context;
};
